import React, { Fragment, useEffect, useRef, useState } from "react";
import CreateIcon from "../../Icons/CreateIcon";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { Popover, Transition } from "@headlessui/react";
import { File, Folder } from "lucide-react";
import useUploadFileHook from "../../customHooks/useUploadFileHook";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import usePostHook from "../../customHooks/usePostHook";
import { postApiData } from "../../helpers/AxiosInstance";
import { useQueryClient } from "@tanstack/react-query";

const AddFolder = ({
  setOpenCreateFolderPopup,
  setOpenQrUploadPopup,
  setQrImage,
  qrImage,
  folderName,
  id,
}: {
  setOpenCreateFolderPopup: (val: boolean) => void;
  id: string;
  setQrImage: React.Dispatch<React.SetStateAction<string>>;
  setOpenQrUploadPopup: React.Dispatch<React.SetStateAction<boolean>>;
  qrImage: string;
  folderName?: string;
}) => {
  const [isUploaded, setIsuploaded] = useState(true);
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["qr-upload"],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, progress } = useUploadFileHook({
    setIsuploaded,
    queryKey: ["file"],
  });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const queryClient = useQueryClient();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => handleUpload(file));
    }
  };

  const generateFileId = () => {
    return (
      "file_" +
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  };

  const toastIdRef = useRef<Record<string, string | number>>({});

  const handleUpload = async (file: File) => {
    const fileId = generateFileId();

    // Show initial toast
    toastIdRef.current[file.name] = toast.info(`Uploading ${file.name} (0%)`, {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });

    await uploadFile(file, fileId, id, folderName, () => {
      toast.update(toastIdRef.current[file.name], {
        render: `${file.name} uploaded successfully!`,
        type: "success",
        autoClose: 3000,
        isLoading: false,
      });
    });
  };

  useEffect(() => {
    Object.entries(toastIdRef.current).forEach(([fileName, toastId]) => {
      if (progress < 100) {
        toast.update(toastId as string | number, {
          render: `Uploading... ${progress.toFixed(0)}%`,
          type: "info",
          isLoading: false,
          autoClose: false,
        });
      }
    });
  }, [progress]);

  const handleQrGenerate = async () => {
    const url = APIS.GENERATE_QR;
    const formData = {
      id: id,
    };
    try {
      const res = await postApiData({
        url,
        formData,
        responseType: "blob",
      });
      if (res?.status === 200) {
        const qrBlob = res.data;
        const qrUrl = URL.createObjectURL(qrBlob);
        setQrImage(qrUrl);
        setOpenQrUploadPopup((prev) => !prev);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate QR");
    }
  };
  useEffect(() => {
    let interval: any;

    if (setOpenQrUploadPopup) {
      interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ["file"] });
      }, 3000); // every 3 sec
    }

    return () => clearInterval(interval);
  }, [setOpenQrUploadPopup]);

  return (
    <div className={tw``}>
      <Popover className={tw`relative`}>
        {() => (
          <>
            <Popover.Button
              className={tw`flex gap-x-2 p-3 bg-[#DEDEDEC2] rounded shadow focus:outline-none`}
            >
              <CreateIcon />
              Add New
            </Popover.Button>
            <Transition
              as={Fragment}
              enter={tw`transition ease-out duration-200`}
              enterFrom={tw`opacity-0 translate-y-1`}
              enterTo={tw`opacity-100 translate-y-0`}
              leave={tw`transition ease-in duration-150`}
              leaveFrom={tw`opacity-100 translate-y-0`}
              leaveTo={tw`opacity-0 translate-y-1`}
            >
              <Popover.Panel
                className={tw`absolute z-10 mt-3 max-w-sm w-[350px] px-4 -mx-[20px]`}
              >
                <div className={tw`w-64 bg-white shadow py-2 rounded`}>
                  <ul>
                    <li>
                      <button
                        type="button"
                        onClick={() => setOpenCreateFolderPopup(true)}
                        className={tw`flex gap-x-2 items-center w-full px-4 py-1 focus:outline-none rounded hover:bg-gray-200`}
                      >
                        <Folder />
                        Create Folder
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={handleUploadClick}
                        className={tw`flex gap-x-2 items-center w-full px-4 py-1 focus:outline-none rounded hover:bg-gray-200`}
                      >
                        <File />
                        Upload File
                      </button>
                    </li>
                    {folderName && (
                      <li>
                        <button
                          onClick={handleQrGenerate}
                          className={tw`flex gap-x-2 items-center w-full px-4 py-1 focus:outline-none rounded hover:bg-gray-200`}
                        >
                          <File />
                          Generate QR
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              </Popover.Panel>
            </Transition>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={handleFileChange}
            />
          </>
        )}
      </Popover>
    </div>
  );
};

export default AddFolder;

